import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import './index.scss';
import {useParams, useHistory} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useSelector} from 'react-redux';
import {useKeyboard} from '../../../../hooks/keyboard';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SelectItem} from '../../../../components/Select';
import {GatewayModel, GatewayNetworkSettingsModel} from '../../../../types/gateway';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {isValidIP, isValidPort, isValidRFAddress, isValidRFNetworkId} from '../../../../utils/validation';
import {useRPC} from '../../../../hooks/rpc';
import {GATEWAY_TYPES} from '../GatewayListScreen';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from "../../../../hooks/localization";
import { useWindowSize } from '../../../../hooks/window-size';
const GATEWAY_NETWORK_SELECTION_ITEMS: SelectItem<boolean>[] = [
    {value: true, label: 'Ethernet'},
    {value: false, label: 'Wi-Fi'},
];

const BACK_URL = '/settings/gateway';

export const GatewayEditScreen = memo(function GatewayEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);
    const [rpc] = useRPC();
    const history = useHistory();

    const gatewayList = useSelector(state => state.control.gatewayList);
    const role = useSelector(state => state.ui.role);

    const [selected, setSelected] = useState<GatewayModel>(null);
    const [name, setName] = useState('');
    const [type, setType] = useState<SelectItem<string>>(null);
    const [isUPnP, setUPnP] = useState(false);
    const [sshStatus, setSshStatus] = useState<boolean>(false)
    const [hasIntercomCamera, setHasIntercomCamera] = useState(false);
    const [newTbellStatus, setNewTbellStatus] = useState(false);
    const [rfNetworkAddress, setRFNetworkAddress] = useState('');
    const [rfNetworkID, setRFNetworkID] = useState('');
    const [rfNetworkChannel, setRFNetworkChannel] = useState('');
    const [homeNetworkOption, setHomeNetworkOption] = useState<SelectItem<boolean>>(null);
    const [homeNetworkIP, setHomeNetworkIP] = useState('');
    // const [homeNetworkPort, setHomeNetworkPort] = useState('');
    const [homeNetworkSubnetMask, setHomeNetworkSubnetMask] = useState('');
    const [homeNetworkDefaultGateway, setHomeNetworkDefaultGateway] = useState('');
    const [remoteNetworkOption, setRemoteNetworkOption] = useState<SelectItem<boolean>>(null);
    const [remoteNetworkIsStatic, setRemoteNetworkIsStatic] = useState(false);
    const [remoteNetworkIP, setRemoteNetworkIP] = useState('');
    const [remoteNetworkPort, setRemoteNetworkPort] = useState('');
    const [remoteNetworkSubnetMask, setRemoteNetworkSubnetMask] = useState('');
    const [remoteNetworkDefaultGateway, setRemoteNetworkDefaultGateway] = useState('');
    const [width, height] = useWindowSize();
    const isPc = useMemo(() => !((width === 1024 && height === 600) || (height === 1024 && width === 600)), [width, height]);

    const ANTENNA_OPTION_LIST: SelectItem<string>[] = useMemo(
        () => [
            {
                label: t("auto"),
                value: "0",
            },
            {
                label: t("first_antenna"),
                value: "1"
            },
            {
                label: t("second_antenna"),
                value: "2"
            }
        ],
        [t]
    )
    const [antenna, setAntenna] = useState<SelectItem<string>>(null)



    const GATEWAY_BAUD_RATE_LIST: SelectItem<string>[] = useMemo(
        () => [
            {
                label: "1",
                value: "1"
            },
            {
                label: "2",
                value: "2"
            },
            {
                label: "3",
                value: "3"
            },
            {
                label: "4",
                value: "4"
            }
        ],
        []
    )
    const [baudRate, setBaudRate] = useState<SelectItem<string>>(null)

    const setRemoteNetworkIsStaticCheckbox = useCallback(() => {
        setRemoteNetworkIsStatic(remoteNetworkIsStatic => !remoteNetworkIsStatic);
    }, []);

    const setUPnPCheckbox = useCallback(() => {
        setUPnP(isUPnP => !isUPnP);
    }, []);

    const setSSHStatus = useCallback(() => {
        setSshStatus(ssh => !ssh)
    }, [])

    const setIntercomCamera = useCallback(() => {
        setHasIntercomCamera(hasIntercomCamera => !hasIntercomCamera);
    }, []);

    const setNewTbell = useCallback(() => {
        setNewTbellStatus(newTbellStatus => !newTbellStatus);
    }, [])

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadInput, setActiveNumpadExtendedInput]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length < 3 || name === 'default') {
            failure(t('pls_enter_a_valid_gateway_name'));

            return false;
        }

        if (type == null || type.value == null) {
            failure(t('pls_enter_a_valid_gateway_type'));

            return false;
        }

        if (!isValidRFAddress(rfNetworkAddress)) {
            failure(t('rf_address_must_be_between_0_255'));

            return false;
        }

        if (!isValidRFNetworkId(rfNetworkID)) {
            failure(t('pls_select_a_valid_rf_network_id'));

            return false;
        }

        if (!isValidRFAddress(rfNetworkChannel)) {
            failure(t('rf_channel_must_be_between_0_255'));

            return false;
        }

        // if (!isValidPort(homeNetworkPort)) {
        //     failure(t('pls_enter_a_valid_home_network_port'));
        //
        //     return false;
        // }

        if (homeNetworkOption == null || homeNetworkOption.value == null) {
            failure(t('pls_select_a_valid_home_network'));

            return false;
        }

        if (!isValidIP(homeNetworkIP)) {
            failure(t('pls_enter_a_valid_home_network_id_address'));

            return false;
        }

        if (!isValidIP(homeNetworkSubnetMask)) {
            failure(t('pls_enter_a_valid_home_network_subnet_mask'));

            return false;
        }

        if (!isValidIP(homeNetworkDefaultGateway)) {
            failure(t('pls_enter_a_valid_home_network_default_gateway'));

            return false;
        }

        if (!isValidPort(remoteNetworkPort)) {
            failure(t('pls_enter_a_valid_remote_network_port'));

            return false;
        }

        if (remoteNetworkOption == null || remoteNetworkOption.value == null) {
            failure(t('pls_select_a_valid_remote_network'));

            return false;
        }

        if (homeNetworkOption.value !== remoteNetworkOption.value) {
            if (remoteNetworkIsStatic) {
                if (!isValidIP(remoteNetworkIP)) {
                    failure(t('pls_enter_a_valid_remote_network_ip_address'));

                    return false;
                }

                if (!isValidIP(remoteNetworkSubnetMask)) {
                    failure(t('pls_enter_a_valid_remote_network_subnet_mask'));

                    return false;
                }

                if (!isValidIP(remoteNetworkDefaultGateway)) {
                    failure(t('pls_enter_a_valid_remote_network_default_gateway'));

                    return false;
                }
            }
        }

        return true;
    }, [
        t,
        failure,
        homeNetworkIP,
        homeNetworkOption,
        // homeNetworkPort,
        homeNetworkSubnetMask,
        homeNetworkDefaultGateway,
        name,
        remoteNetworkIP,
        remoteNetworkOption,
        remoteNetworkPort,
        remoteNetworkIsStatic,
        remoteNetworkSubnetMask,
        remoteNetworkDefaultGateway,
        rfNetworkAddress,
        rfNetworkChannel,
        rfNetworkID,
        type,
    ]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const homeNetworkSettings: GatewayNetworkSettingsModel = {
            is_ethernet: homeNetworkOption.value,
            is_static: true,
            ip_address: homeNetworkIP,
            // port: Number(homeNetworkPort),
            port: 8088,
            subnet_mask: homeNetworkSubnetMask,
            default_gateway: homeNetworkDefaultGateway,
        };

        let sipSettings = selected?.settings.sip;
        let intercomSettings = selected?.settings.intercom;

        if (!selected) {
            sipSettings = {
                flat_name: '',
                password: '',
                server_ip: '',
                server_port: 0,
                user_id: 0,
                sip_status: false,
                has_sip: false,
            };

            intercomSettings = {
                has_usb_camera: hasIntercomCamera,
                new_tbell_status: newTbellStatus
            };
        }

        const gatewayModel: GatewayModel = {
            id: selected ? selected.id : null,
            name,
            type_id: type.value,
            upnp: isUPnP,
            settings: {
                ...selected ? selected.settings : {},
                intercom: {
                    ...intercomSettings,
                    has_usb_camera: hasIntercomCamera,
                    new_tbell_status: newTbellStatus
                },
                sip: sipSettings,
                home_network: homeNetworkSettings,
                rf: {
                    address: Number(rfNetworkAddress),
                    network_id: Number(rfNetworkID),
                    channel: Number(rfNetworkChannel),
                    antenna_diversity: Number(antenna?.value),
                    baud_rate_selection: Number(baudRate?.value)
                },
                port_settings: {
                    ssh_status: sshStatus
                },
                remote_network: homeNetworkOption.value !== remoteNetworkOption.value ? {
                    is_ethernet: remoteNetworkOption.value,
                    is_static: remoteNetworkIsStatic,
                    ip_address: remoteNetworkIP,
                    port: Number(remoteNetworkPort),
                    subnet_mask: remoteNetworkSubnetMask,
                    default_gateway: remoteNetworkDefaultGateway,
                } : {
                    ...homeNetworkSettings,
                    port: Number(remoteNetworkPort),
                },
                setup_mode: false,
            },
        };

        const isHomeIPChanged = selected && selected.settings?.home_network.ip_address !== homeNetworkIP;
        rpc.updateSSHPortStatus(gatewayModel?.id, gatewayModel.settings?.port_settings?.ssh_status)
        return rpc.updateGateway(gatewayModel)
            .then(() => {
                success(t(gatewayModel.id ? 'gateway_successfully_edited' : 'gateway_successfully_created'));
                if (isHomeIPChanged && isPc) {
                    window.location.href = `http://${homeNetworkIP}:8088`;
                } else {
                    onBackPressed();
                }
            })
            .catch(failure);
    }, [
        t,
        rpc,
        success,
        failure,
        onBackPressed,
        selected,
        isFieldsValid,
        name,
        type,
        isUPnP,
        hasIntercomCamera,
        newTbellStatus,
        rfNetworkAddress,
        rfNetworkID,
        rfNetworkChannel,
        homeNetworkOption,
        homeNetworkIP,
        baudRate,
        // homeNetworkPort,
        homeNetworkSubnetMask,
        homeNetworkDefaultGateway,
        remoteNetworkOption,
        remoteNetworkIsStatic,
        remoteNetworkIP,
        remoteNetworkPort,
        remoteNetworkSubnetMask,
        remoteNetworkDefaultGateway,
        sshStatus,
        antenna,
        isPc
    ]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteGateway(selected.id)
            .then(() => {
                success(t('gateway_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, success, failure, onBackPressed, rpc, selected]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type='gateway'
                    onPress={onDeletePressed}
                    questionText={t('are_you_sure_to_delete_gateway')}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue',{name:selected?.name, message})}/>,
        },
        {
            text: t('wifi_settings'),
            url: `/settings/gateway/${selected ? selected.id : ''}/wifi`,
            className: 'blue',
            hidden: !(selected),
        },
        {
            text: t('sip_settings'),
            url: `/settings/gateway/${selected ? selected.id : ''}/sip`,
            className: 'blue',
            hidden: !(selected),
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onCreatePressed, onDeletePressed, selected]);


    const GATEWAY_TYPE_SELECTION_ITEMS = useMemo<SelectItem<string>[]>(() =>
        Object.entries(GATEWAY_TYPES).map(([id, name]) => ({
            value: id,
            label: t(name)
        })), [t]);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setType(GATEWAY_TYPE_SELECTION_ITEMS[0]);
        setHomeNetworkOption(GATEWAY_NETWORK_SELECTION_ITEMS[0]);
        setRemoteNetworkOption(GATEWAY_NETWORK_SELECTION_ITEMS[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const gateway = gatewayList.find(gateway => gateway.id === params['id']);
        if (gateway == null) {
            return;
        }

        setSelected(gateway);
        setName(gateway.name);
        setType(GATEWAY_TYPE_SELECTION_ITEMS.find(option => option.value === gateway.type_id));
        setUPnP(gateway.upnp);
        setHasIntercomCamera(gateway.settings.intercom.has_usb_camera);
        setNewTbellStatus(gateway.settings.intercom.new_tbell_status);
        setRFNetworkAddress(gateway.settings.rf.address.toString());
        setRFNetworkID(gateway.settings.rf.network_id.toString());
        setRFNetworkChannel(gateway.settings.rf.channel.toString());
        setSshStatus(gateway.settings.port_settings.ssh_status)
        setAntenna(ANTENNA_OPTION_LIST.find(antenna => antenna?.value === String(gateway?.settings?.rf?.antenna_diversity)))
        setBaudRate(GATEWAY_BAUD_RATE_LIST.find(baudRate => baudRate?.value === String(gateway?.settings?.rf?.baud_rate_selection)))
        if (gateway.settings.home_network) {
            setHomeNetworkOption(GATEWAY_NETWORK_SELECTION_ITEMS.find(
                option => option.value === gateway.settings.home_network.is_ethernet));
            setHomeNetworkIP(gateway.settings.home_network.ip_address);
            // setHomeNetworkPort(gateway.settings.home_network.port.toString());
            setHomeNetworkSubnetMask(gateway.settings.home_network.subnet_mask);
            setHomeNetworkDefaultGateway(gateway.settings.home_network.default_gateway);
        }

        if (gateway.settings.remote_network) {
            setRemoteNetworkOption(GATEWAY_NETWORK_SELECTION_ITEMS.find(
                option => option.value === gateway.settings.remote_network.is_ethernet));
            setRemoteNetworkPort(gateway.settings.remote_network.port.toString());
            if (gateway.settings.home_network.is_ethernet !== gateway.settings.remote_network.is_ethernet) {
                setRemoteNetworkIsStatic(gateway.settings.remote_network.is_static);
                if (gateway.settings.remote_network.is_static) {
                    setRemoteNetworkIP(gateway.settings.remote_network.ip_address);
                    setRemoteNetworkSubnetMask(gateway.settings.remote_network.subnet_mask);
                    setRemoteNetworkDefaultGateway(gateway.settings.remote_network.default_gateway);
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewayList]);

    useEffect(() => {
        rpc.getSSHPortStatus()
    }, [rpc])


    useEffect(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
        // eslint-disable-next-line
    }, [remoteNetworkIsStatic]);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_gateway_name')}
                            label={t('gateway_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('gateway_type')}
                            options={GATEWAY_TYPE_SELECTION_ITEMS}
                            onChange={setType}
                            value={type}/>
                        <SettingCheckBoxComponent
                            label={t('upnp')}
                            set={setUPnPCheckbox}
                            value={isUPnP}/>
                        {/*<SettingInputComponent*/}
                        {/*    placeholder={t('write_port')}*/}
                        {/*    label={t('home_net_port')}*/}
                        {/*    value={homeNetworkPort}*/}
                        {/*    setValue={setHomeNetworkPort}*/}
                        {/*    setActiveInput={setActiveNumpadInput}*/}
                        {/*    onBlur={onBlur}/>*/}
                        <SettingSelectComponent
                            label={t('home_network')}
                            options={GATEWAY_NETWORK_SELECTION_ITEMS}
                            onChange={setHomeNetworkOption}
                            value={homeNetworkOption}/>
                        <SettingInputComponent
                            placeholder={t('write_ip')}
                            label={t('network_ip')}
                            value={homeNetworkIP}
                            setValue={setHomeNetworkIP}
                            setActiveInput={setActiveNumpadExtendedInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t('write_subnetwork_mask')}
                            label={t('subnetwork_mask')}
                            value={homeNetworkSubnetMask}
                            setValue={setHomeNetworkSubnetMask}
                            setActiveInput={setActiveNumpadExtendedInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t('write_default_gateway')}
                            label={t('default_gateway')}
                            value={homeNetworkDefaultGateway}
                            setValue={setHomeNetworkDefaultGateway}
                            setActiveInput={setActiveNumpadExtendedInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t('write_port')}
                            label={t('remote_net_port')}
                            value={remoteNetworkPort}
                            setValue={setRemoteNetworkPort}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('remote_network')}
                            options={GATEWAY_NETWORK_SELECTION_ITEMS}
                            onChange={setRemoteNetworkOption}
                            value={remoteNetworkOption}/>
                    </div>
                    <div className='content-wrapper'>
                        <SettingCheckBoxComponent
                            label={t('t_bell')}
                            set={setIntercomCamera}
                            value={hasIntercomCamera}/>
                        {renderIf(hasIntercomCamera)(() => 
                            <SettingCheckBoxComponent
                                label={t("t_bell_v2")}
                                set={setNewTbell}
                                value={newTbellStatus}/>
                        )}
                        <SettingInputComponent
                            placeholder={t('write_rf_address')}
                            readOnly
                            disabled
                            label={t('rf_address')}
                            value={rfNetworkAddress}
                            setValue={setRFNetworkAddress}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t('write_rf_network_id')}
                            label={t('rf_network_id')}
                            value={rfNetworkID}
                            setValue={setRFNetworkID}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t('write_rf_channel')}
                            label={t('rf_channel')}
                            value={rfNetworkChannel}
                            setValue={setRFNetworkChannel}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                         <SettingSelectComponent
                            label={t('baud_rate')}
                            value={baudRate}
                            options={GATEWAY_BAUD_RATE_LIST}
                            onChange={setBaudRate} />
                         <SettingSelectComponent
                            label={t('antenna')}
                            value={antenna}
                            options={ANTENNA_OPTION_LIST}
                            onChange={setAntenna} />


                        {renderIf(role === 'developer')(()=>(
                        <SettingCheckBoxComponent
                            label={t('ssh_status')}
                            set={setSSHStatus}
                            value={sshStatus}/>))}
                        {
                            renderIf(homeNetworkOption != null &&
                                remoteNetworkOption != null &&
                                homeNetworkOption.value !== remoteNetworkOption.value)(() =>
                                <>
                                    {
                                        renderIf(remoteNetworkIsStatic)(() =>
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t("write_ip")}
                                                    label={t('network_ip')}
                                                    value={remoteNetworkIP}
                                                    setValue={setRemoteNetworkIP}
                                                    setActiveInput={setActiveNumpadExtendedInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_subnetwork_mask')}
                                                    label={t('subnetwork_mask')}
                                                    value={remoteNetworkSubnetMask}
                                                    setValue={setRemoteNetworkSubnetMask}
                                                    setActiveInput={setActiveNumpadExtendedInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_default_gateway')}
                                                    label={t('default_gateway')}
                                                    value={remoteNetworkDefaultGateway}
                                                    setValue={setRemoteNetworkDefaultGateway}
                                                    setActiveInput={setActiveNumpadExtendedInput}
                                                    onBlur={onBlur}/>
                                            </>,
                                        )
                                    }
                                    <SettingCheckBoxComponent
                                        label={t('static_ip')}
                                        set={setRemoteNetworkIsStaticCheckbox}
                                        value={remoteNetworkIsStatic}/>
                                </>,
                            )
                        }


                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
//0 otomatik,  1.Anten
